import { DateRange } from '@angular/material/datepicker';
import {
    CompositeFilter,
    FilterCodec,
    FormatedSavedFilter,
    NamedFilter,
    SavedFilter,
    SavedFilterService
} from '@teammood/filter';
import { DEFAULT_DATE_RANGE_PRESET_IDS, DateRangePickerPresetId, TmDateRangePickerComponent } from '@teammood/shared/ui/date-range-picker';
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { FiltersService } from 'app/frontend/lib/filter/filters.service';
import { UserService } from 'app/frontend/lib/shared/util/user';
import { StatsService } from '../../analytics/stats/stats.service';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SavedFilterSelectorComponent } from './saved-filter-selector/saved-filter-selector.component';
import { SavedFilterManagerComponent } from './saved-filter-manager/saved-filter-manager.component';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, tap } from 'rxjs';

@Component({
    selector: 'saved-filter',
    templateUrl: './saved-filter.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SavedFilterSelectorComponent,
        SavedFilterManagerComponent,
        TmDateRangePickerComponent
    ]
})
export class SavedFilterComponent implements OnInit {
    presetIds: DateRangePickerPresetId[];
    maxDate: Date;
    namedFilters: FormatedSavedFilter[];
    filterManagerOpen: boolean;
    managerMode: 'create' | 'update' | 'forward' | 'inactive';
    isFilterUpdated: boolean;
    dateIntervalCtrl = new FormControl<DateRange<Date>>(null);

    @Input() pageTitle: string;
    @Input() selectedNamedFilter: FormatedSavedFilter;
    @Input() dateInterval: DateRange<Date>;
    @Input() manageDate: boolean = true;
    @Input() onlyWhereIAMAdmin: boolean = false;
    @Input() managerInDialog: boolean = false;

    @Output() filterChange = new EventEmitter<SavedFilterChange>();

    constructor(
        private translateService: TranslateService,
        private filterCodec: FilterCodec, 
        private filtersService: FiltersService, 
        private userService: UserService, 
        private statsService: StatsService, 
        private snackBarService: SnackBarService, 
        private savedFilterService: SavedFilterService
    ) {}
    
    async ngOnInit() {
        if(this.manageDate){
            this.dateIntervalCtrl.setValue(this.dateInterval); 

            this.dateIntervalCtrl.valueChanges.pipe(
                distinctUntilChanged(),
                tap((dateInterval) => {
                    this.onDateRangeChange(dateInterval)
                }),
            ).subscribe();
    
            this.presetIds = DEFAULT_DATE_RANGE_PRESET_IDS;
            this.maxDate = new Date();
        }
        
        this.filterManagerOpen = false;
        this.isFilterUpdated = false;
        this.managerMode = 'inactive';

        const user = await this.userService.get();
        const filtersData = await this.filtersService.loadFilters(this.onlyWhereIAMAdmin);
        const addNewFilter = !this.managerInDialog // if we are in dialog we don't want to add new filter by default (used in taskbook pages)
        
        if(filtersData){
            const filters = this.filtersService.formatFilters(filtersData, this.create, this.update, addNewFilter);
            this.namedFilters = filters;
            // On load I use select default filter or filter given in the url as new custom filter - or as my filter if it's the same
            const defaultFilter = this.filtersService.getDefaultFilter(filters);
            const encodeUrlFilter = this.savedFilterService.getCurrentFilter();
            const decodedUrlFilter = this.filterCodec.decode(encodeUrlFilter);
            const isEmptyDecodedUrlFilter = !decodedUrlFilter?.predicates || decodedUrlFilter.predicates.length === 0;
            const indexOfUrlFilterInFilters = filters.findIndex(filter => filter.jsonFilter === JSON.stringify(decodedUrlFilter));
            // compare decoded filters as strings for deep difference object comparing
            if(isEmptyDecodedUrlFilter){
                this.selectedNamedFilter = defaultFilter as FormatedSavedFilter;
                const filter = this.selectedNamedFilter.decodedFilter;
                this.savedFilterService.saveFilter(filter);
                this.emitChange();
                this.setManagerMode();
            }
            else if(indexOfUrlFilterInFilters !== -1){
                this.selectedNamedFilter = filters[indexOfUrlFilterInFilters];
                const filter = this.selectedNamedFilter.decodedFilter;
                this.savedFilterService.saveFilter(filter);
                this.emitChange();
                this.setManagerMode();
            }
            else{
                this.all_org_units(`uid=${user.id}`).then(all_org_units => {
                    // use new filter item to display the filter sent via the url
                    const newFilter = filters[filters.length-1]; // new filter = last created with formatFilters
                    newFilter.decodedFilter = decodedUrlFilter;
                    newFilter.filter = encodeUrlFilter;
                    //check if org unit of decodedUrlFilter are among availableOrgUnits (i.e. user is allowed to see the data)
                    if(this.filtersService.arePredicatesAmongAllowedOrgUnits(decodedUrlFilter.predicates, all_org_units)){
                        //todo fix why this.selectedNamedFilter not actualise in view
                        this.selectedNamedFilter = newFilter;
                        this.filterManagerOpen = true;
                        this.managerMode = 'forward';
                        this.snackBarService.success('Forwarded filter data loaded!');
                    }else{
                        this.snackBarService.error('Sorry, you are not authorized to access the data for this filter.');
                        this.selectedNamedFilter = defaultFilter as FormatedSavedFilter;
                    }

                    const filter = this.selectedNamedFilter.decodedFilter;
                    this.savedFilterService.saveFilter(filter);
                    this.emitChange();
                    this.setManagerMode();
                })
            }
        }else{
            this.namedFilters = [];
        }
    }
            
    async all_org_units(url_params: string) {
        return await this.statsService.all_org_units(url_params);
    }
          
    async create(filter: SavedFilter){
        const currentFilterName = filter.name;
        await this.filtersService.createFilter(filter).then(result => {
            this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update, !this.managerInDialog);
            this.selectedNamedFilter = this.namedFilters.find(filter => filter.name === currentFilterName);
            this.snackBarService.success(this.translateService.instant('filters_setting.success_create'));
        })
        this.filterManagerOpen = false;
    }

    update(filter: SavedFilter){
        const currentFilterId = filter.id;

        this.filtersService.updateFilter(filter).then(result => {
            this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update, !this.managerInDialog);
            this.selectedNamedFilter = this.namedFilters.find(filter => filter.id === currentFilterId);
            this.snackBarService.success(this.translateService.instant('filters_setting.success_update'));
        })
        this.filterManagerOpen = false;
    }

    onCreateOrUpdateFilter (filter: SavedFilter) {
        if (filter.id) {
            this.update(filter);
        } else {
            this.create(filter);
        }
    }

    set_as_default (filterId) {
        this.filtersService.setAsDefault(filterId).then(result => {
            this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update, !this.managerInDialog);
            this.selectedNamedFilter = this.namedFilters.find(filter => filter.id === filterId);
        })
    }

    onSelectedFilterChange (filter: FormatedSavedFilter) {
        if (filter) {
            
            this.selectedNamedFilter = filter as FormatedSavedFilter;
            //update filter list -> reset filter change unsaved
            this.filtersService.loadFilters(this.onlyWhereIAMAdmin).then( result => {
                this.namedFilters = this.filtersService.formatFilters(result, this.create, this.update, !this.managerInDialog);
            })
            this.setManagerMode();
            this.onFilterChange(filter.decodedFilter);
        }
    }

    setManagerMode(){
        if(this.selectedNamedFilter && this.selectedNamedFilter.baseNamedFilter === false){
            if(this.selectedNamedFilter.createLink){
                //reset forward filter or previous new filter
                // this.selectedNamedFilter.filter = undefined;
                // this.selectedNamedFilter.decodedFilter = {
                //     predicates: []
                // };
                this.managerMode = 'create';
                this.filterManagerOpen = true;
            }else{
                this.managerMode = 'update';
            }
        }else{
            this.managerMode = 'inactive';
        }
    }

    onFilterChange (filter: CompositeFilter) {
        if (filter) {
            // put filter in url and load data
            this.savedFilterService.saveFilter(filter);
            this.selectedNamedFilter.decodedFilter = filter;
            this.emitChange();
        }
    }

    // date picker management
    onDateRangeChange(dateRange: DateRange<Date>) {
        this.dateInterval = dateRange;
        this.savedFilterService.saveDateRange(this.dateInterval);
        this.emitChange();
    }

    private emitChange() {
        if (this.selectedNamedFilter.decodedFilter.predicates.length > 0) {
            this.filterChange.emit({ dateInterval: this.dateInterval, filter: this.selectedNamedFilter });
        }
    }
}

export interface SavedFilterChange {
    dateInterval: DateRange<Date>,
    filter: FormatedSavedFilter
}
